import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import ActionButton from '../../../../ActionButton';
import ErrorForm from '../../../../ErrorForm';
import { ReactComponent as AttachmentIcon } from '../../../../../assetsNew/icons/attachment.svg';
import { ReactComponent as FileDocumentIcon } from '../../../../../assetsNew/icons/icon-file-document.svg';

import './_batchMessageAttachments.scss';
import { Tooltip } from '@mui/material';

const MAX_FILES = 30;
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  const kb = Math.round(bytes / 1024);
  if (kb < 1024) return `${kb} kb`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const BatchMessageAttachments = forwardRef((_, ref) => {
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [warnings, setWarnings] = useState([]);

  useImperativeHandle(ref, () => ({
    reset: () => {
      setAttachments([]);
      setWarnings([]);
    },
    getFiles: () => attachments
  }));

  const handleFileChange = e => {
    const incoming = Array.from(e.target.files);
    const nextWarnings = [];

    const tooLarge = incoming.filter(file => file.size > MAX_FILE_SIZE_BYTES);
    if (tooLarge.length > 0) {
      nextWarnings.push(
        `${tooLarge.length === 1 ? 'One file exceeds' : `${tooLarge.length} files exceed`} the 20 MB limit and ${tooLarge.length === 1 ? 'was' : 'were'} not added.`
      );
    }

    const existingKeys = new Set(attachments.map(f => `${f.name}_${f.size}`));
    const valid = incoming.filter(
      file =>
        ALLOWED_MIME_TYPES.includes(file.type) &&
        file.size <= MAX_FILE_SIZE_BYTES &&
        !existingKeys.has(`${file.name}_${file.size}`)
    );

    const combined = [...attachments, ...valid];
    if (combined.length > MAX_FILES) {
      nextWarnings.push(`You can add a maximum of ${MAX_FILES} files. Some files were not added.`);
      setAttachments(combined.slice(0, MAX_FILES));
    } else {
      setAttachments(combined);
    }

    setWarnings(nextWarnings);
    e.target.value = '';
  };

  const removeAttachment = index => {
    setWarnings([]);
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="batchMessageAttachments">
      <div className="batchMessageAttachments-row">
        <ActionButton
          variant="outlined"
          title="Add attachments"
          startIcon={<AttachmentIcon className="batchMessageAttachments-icon" />}
          disabled={attachments.length >= MAX_FILES}
          onClick={() => {
            setWarnings([]);
            fileInputRef.current && fileInputRef.current.click();
          }}
        />
        <span className="batchMessageAttachments-hint">
          Max 20 MB<br />JPG, PNG, PDF, DOCX
        </span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.pdf,.docx"
        className="batchMessageAttachments-input"
        onChange={handleFileChange}
      />

      {attachments.length > 0 && (
        <div className="batchMessageAttachments-list">
          {attachments.map((file, index) => (
            <div key={index} className="batchMessageAttachments-chip">
              <button
                type="button"
                className="batchMessageAttachments-chip-remove"
                onClick={() => removeAttachment(index)}
              >
                <CloseIcon className="batchMessageAttachments-chip-remove-icon" />
              </button>
              <div className="batchMessageAttachments-chip-file-icon">
                <FileDocumentIcon />
              </div>
              <Tooltip title={file.name} placement="top">
                <span className="batchMessageAttachments-chip-name">{file.name}</span>
              </Tooltip>
              <span className="batchMessageAttachments-chip-size">{formatFileSize(file.size)}</span>
            </div>
          ))}
        </div>
      )}

      {warnings.length > 0 && (
        <div className="batchMessageAttachments-warnings">
          {warnings.map((warning, i) => (
            <ErrorForm key={i} message={warning} />
          ))}
        </div>
      )}
    </div>
  );
});

BatchMessageAttachments.displayName = 'BatchMessageAttachments';

export default BatchMessageAttachments;
